import { useState, useEffect, useCallback } from 'react'
import {
  getInvites,
  getInvite,
  type InviteSummary,
  type InviteDetail,
  type InviteCreateResponse,
} from '../../services/adminApi'
import { CustomButton } from '../../components/common/CustomButton'
import { CustomTextInput } from '../../components/common/CustomTextInput'
import { usePermission } from '../../hooks/usePermission'
import { CreateInviteModal } from './CreateInviteModal'
import { InviteCreatedModal } from './InviteCreatedModal'
import { InviteDetailModal } from './InviteDetailModal'
import { SendInvitesModal } from './SendInvitesModal'

const statusColors: Record<string, string> = {
  pending: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  accepted: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  expired: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
  revoked: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
}

const statusOptions = [
  { value: '', label: 'All Statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'expired', label: 'Expired' },
  { value: 'revoked', label: 'Revoked' },
]

export function Invites() {
  const [invites, setInvites] = useState<InviteSummary[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')

  const [showCreateModal, setShowCreateModal] = useState(false)
  const [createdResult, setCreatedResult] = useState<InviteCreateResponse | null>(null)
  const [selectedInvite, setSelectedInvite] = useState<InviteDetail | null>(null)
  const [showSendModal, setShowSendModal] = useState(false)

  const canCreate = usePermission('invites', 'create')
  const canSend = usePermission('invites', 'send')

  const loadInvites = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await getInvites({ search, status: statusFilter, page })
      if (response.ok && response.data) {
        setInvites(response.data.items)
        setTotal(response.data.total)
        setTotalPages(response.data.total_pages)
      } else {
        setError(response.error?.message || 'Failed to load invites')
      }
    } catch {
      setError('Failed to connect to API')
    } finally {
      setLoading(false)
    }
  }, [search, statusFilter, page])

  useEffect(() => {
    loadInvites()
  }, [loadInvites])

  const handleViewInvite = async (id: string) => {
    const response = await getInvite(id)
    if (response.ok && response.data) {
      setSelectedInvite(response.data)
    }
  }

  const handleInvitesCreated = (result: InviteCreateResponse) => {
    setShowCreateModal(false)
    setCreatedResult(result)
    loadInvites()
  }

  const pendingWithEmail = invites.filter((i) => i.status === 'pending' && i.email && !i.email_sent_at)

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="font-heading text-2xl font-bold text-gray-900 dark:text-gray-100">Invites</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">{total} total invites</p>
        </div>
        <div className="flex gap-2">
          {canSend && pendingWithEmail.length > 0 && (
            <CustomButton onClick={() => setShowSendModal(true)} variant="secondary">
              Send Emails ({pendingWithEmail.length})
            </CustomButton>
          )}
          {canCreate && (
            <CustomButton onClick={() => setShowCreateModal(true)} variant="primary">
              Create Invites
            </CustomButton>
          )}
        </div>
      </div>

      <div className="mb-6 flex flex-wrap gap-4">
        <div className="w-64">
          <CustomTextInput
            label=""
            placeholder="Search by email..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1) }}
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(1) }}
          className="rounded-xl border border-gray-300 dark:border-[#3d2c4f] bg-white dark:bg-[#0f0d12] px-4 py-2 text-sm text-gray-900 dark:text-gray-100 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
        >
          {statusOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>

      {error && (
        <div className="mb-6 rounded-xl bg-red-50 dark:bg-red-900/20 p-4 text-red-700 dark:text-red-300">{error}</div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        </div>
      ) : invites.length === 0 ? (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">No invites found</div>
      ) : (
        <div className="space-y-3">
          {invites.map((invite) => (
            <div
              key={invite.id}
              className="rounded-2xl border border-border-card dark:border-gray-700 bg-white dark:bg-[#1a1520] p-4 cursor-pointer hover:border-primary/50 dark:hover:border-[#7f4f9a]/50 transition-colors"
              onClick={() => handleViewInvite(invite.id)}
            >
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-medium text-gray-900 dark:text-gray-100">
                    {invite.email || 'Generic invite'}
                  </p>
                  <p className="text-xs font-mono text-gray-400 dark:text-gray-500 mt-0.5">{invite.id}</p>
                </div>
                <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${statusColors[invite.status]}`}>
                  {invite.status}
                </span>
              </div>
              <div className="mt-3 flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
                <span>Created {new Date(invite.created_at).toLocaleDateString()}</span>
                <span>Expires {new Date(invite.expires_at).toLocaleDateString()}</span>
                {invite.accepted_at && (
                  <span>Accepted {new Date(invite.accepted_at).toLocaleDateString()}</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {totalPages > 1 && (
        <div className="mt-6 flex items-center justify-center gap-2">
          <CustomButton onClick={() => setPage((p) => p - 1)} disabled={page <= 1} variant="secondary">
            Previous
          </CustomButton>
          <span className="text-sm text-gray-600 dark:text-gray-400">Page {page} of {totalPages}</span>
          <CustomButton onClick={() => setPage((p) => p + 1)} disabled={page >= totalPages} variant="secondary">
            Next
          </CustomButton>
        </div>
      )}

      {showCreateModal && (
        <CreateInviteModal
          onClose={() => setShowCreateModal(false)}
          onCreated={handleInvitesCreated}
        />
      )}

      {createdResult && (
        <InviteCreatedModal
          result={createdResult}
          onClose={() => setCreatedResult(null)}
        />
      )}

      {selectedInvite && (
        <InviteDetailModal
          invite={selectedInvite}
          onClose={() => setSelectedInvite(null)}
          onUpdate={loadInvites}
        />
      )}

      {showSendModal && (
        <SendInvitesModal
          invites={pendingWithEmail}
          onClose={() => setShowSendModal(false)}
          onSent={loadInvites}
        />
      )}
    </div>
  )
}
